import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLazyQuery, useQuery } from '@apollo/client'
import { faDownload, faFolderArrowDown } from '@fortawesome/pro-solid-svg-icons'
import { observer } from 'mobx-react'

import { Alert, Widget } from 'Components/_theme'
import { COLLAPSED_REPORT_ITEMS_LIMIT } from 'Components/DownloadDataErasureReportsCard/DownloadDataErasureReportsCard.constants'
import {
  type DownloadDataErasureReportsCardProps,
  ReportItemStatusEnum,
} from 'Components/DownloadDataErasureReportsCard/DownloadDataErasureReportsCard.types'
import { DownloadDateErasureReportsCardActions } from 'Components/DownloadDataErasureReportsCard/DownloadDateErasureReportsCardActions'
import { DownloadDateErasureReportsCardItem } from 'Components/DownloadDataErasureReportsCard/DownloadDateErasureReportsCardItem'

import { useTheme } from 'Hooks'

import type {
  getReportStatus,
  getReportStatus_getReportStatus_reports,
  getReportStatusVariables,
} from 'Constants/graphql/queries/detail/__generated__/getReportStatus'
import type {
  requestReportDownload_requestReportDownload,
  requestReportDownloadVariables,
} from 'Constants/graphql/queries/detail/__generated__/requestReportDownload'
import { GET_REPORT_STATUS } from 'Constants/graphql/queries/detail/GetReportStatus'
import { REQUEST_REPORT_DOWNLOAD } from 'Constants/graphql/queries/detail/RequestReportDownload'

import DownloadZipFileImage from 'Assets/images/layout/downloadzipfile.svg'
import DownloadZipFileImageTelekom from 'Assets/images/layout/downloadzipfiletelekom.svg'

export const DownloadDataErasureReportsCard = observer(
  (props: DownloadDataErasureReportsCardProps) => {
    const { organisationId } = props

    const [isExpanded, setIsExpanded] = useState(false)
    const [showError, setShowError] = useState(false)

    const { isTelekomTheme } = useTheme()

    const {
      data: reportStatusData,
      startPolling,
      stopPolling,
      refetch: refetchReportStatusData,
    } = useQuery<getReportStatus, getReportStatusVariables>(GET_REPORT_STATUS, {
      pollInterval: 10000,
      variables: {
        organisationID: organisationId,
      },
    })

    const [requestReportDownload, { error: requestReportDownloadError }] =
      useLazyQuery<
        requestReportDownload_requestReportDownload,
        requestReportDownloadVariables
      >(REQUEST_REPORT_DOWNLOAD, {
        errorPolicy: 'all',
        onCompleted: () => {
          refetchReportStatusData()
        },
        onError: () => {
          refetchReportStatusData()
        },
      })

    const intl = useIntl()

    const downloadAllReportItem = useMemo(
      () =>
        reportStatusData?.getReportStatus?.reports.find(
          report => report.year === 'all',
        ),
      [reportStatusData],
    )

    const yearlyReports = useMemo(
      () =>
        reportStatusData?.getReportStatus?.reports.filter(
          report => report.year !== 'all',
        ),
      [reportStatusData],
    )

    const isExpandable = useMemo(
      () =>
        !!yearlyReports?.length &&
        yearlyReports.length > COLLAPSED_REPORT_ITEMS_LIMIT,
      [yearlyReports],
    )

    // This will be handled in later stage, so for now its hardcoded to false
    const isDownloadAllAvailable = false
    // const isDownloadAllAvailable = useMemo(
    //   () => !!yearlyReports?.length && yearlyReports.length > 1,
    //   [yearlyReports],
    // )

    const toggleExpand = useCallback(() => setIsExpanded(curr => !curr), [])

    const handleRequestDownload = useCallback(
      (year: getReportStatus_getReportStatus_reports['year']) => {
        requestReportDownload({
          variables: {
            organisationID: organisationId,
            year,
          },
        })
      },
      [organisationId, requestReportDownload],
    )

    const handleUrlDownload = useCallback(
      (url: getReportStatus_getReportStatus_reports['url']) => {
        if (!url) {
          setShowError(true)

          return
        }

        const anchor = document.createElement('a')

        anchor.href = url
        anchor.download = ''
        anchor.target = '_blank'
        anchor.click()
      },
      [],
    )

    const visibleReports = isExpanded
      ? yearlyReports
      : yearlyReports?.slice(0, COLLAPSED_REPORT_ITEMS_LIMIT)

    const showActionsContainer = isExpandable || isDownloadAllAvailable

    const hasPendingReports = useMemo(
      () =>
        !!reportStatusData?.getReportStatus?.reports.some(
          report => report.status === ReportItemStatusEnum.PENDING,
        ),
      [reportStatusData],
    )

    useEffect(() => {
      if (hasPendingReports) {
        startPolling(10000)
      } else {
        stopPolling()
      }

      return () => {
        stopPolling()
      }
    }, [hasPendingReports, startPolling, stopPolling])

    if (!yearlyReports?.length) {
      return null
    }

    return (
      <Widget
        className="mb-6"
        content={
          <FormattedMessage id="DownloadDataErasureReportsCard_content" />
        }
        imageSrc={
          isTelekomTheme ? DownloadZipFileImageTelekom : DownloadZipFileImage
        }
        primaryIcon={faFolderArrowDown}
        title={intl.formatMessage({
          id: 'DownloadDataErasureReportsCard_title',
        })}
      >
        {showError ||
          (requestReportDownloadError && (
            <Alert className="mb-2" iconLeft={faDownload} variant="danger">
              <FormattedMessage id="DownloadDataErasureReportsCard_generic_error" />
            </Alert>
          ))}

        {visibleReports?.map(report => (
          <DownloadDateErasureReportsCardItem
            key={report.year}
            hasPendingReports={hasPendingReports}
            onReportDownload={handleUrlDownload}
            onReportRequest={handleRequestDownload}
            report={report}
          />
        ))}

        {showActionsContainer && (
          <DownloadDateErasureReportsCardActions
            downloadAllReportItem={downloadAllReportItem}
            hasPendingReports={hasPendingReports}
            isDownloadAllAvailable={isDownloadAllAvailable}
            isExpandable={isExpandable}
            isExpanded={isExpanded}
            onReportDownload={handleUrlDownload}
            onReportRequest={handleRequestDownload}
            toggleExpand={toggleExpand}
          />
        )}
      </Widget>
    )
  },
)
